// Every sign-in step as a screen. The words are v1's; the structure is new.
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { AuthFlow, AuthState } from "./auth-flow";
import { lastEmail } from "./auth-flow";
import { Button } from "@/ui/Button";
import { enterPractice } from "@/practice/mode";
import styles from "./SignInScreen.module.css";

export function SignInScreen({ flow, state }: { flow: AuthFlow; state: AuthState }) {
  return (
    <div className={styles.wrap}>
      <div className={styles.card} role="region" aria-live="polite">
        <Step flow={flow} state={state} />
      </div>
      {state.step === "signed-out" && (
        <section className={styles.card} aria-labelledby="practice-head">
          <h2 id="practice-head">Just trying it out?</h2>
          <p className="hint">Practice mode is a sample warehouse in this browser: shelves, vans, wire, tools and a few days of history. Try anything. Nothing is saved and nothing reaches the real inventory.</p>
          <div className={styles.practice}>
            <Button onClick={() => enterPractice("tech")}>Practise as a tech</Button>
            <Button onClick={() => enterPractice("admin")}>Practise as an admin</Button>
          </div>
        </section>
      )}
    </div>
  );
}

function Step({ flow, state }: { flow: AuthFlow; state: AuthState }) {
  switch (state.step) {
    case "loading":
      return <p role="status">Signing you in…</p>;
    case "signed-out":
      return <PasswordStep flow={flow} message={state.message} />;
    case "email-code":
      return <EmailCodeStep flow={flow} email={state.email} message={state.message} />;
    case "mfa-code":
      return <MfaCodeStep flow={flow} factorId={state.factorId} message={state.message} />;
    case "mfa-enrol":
      return <EnrolStep flow={flow} state={state} />;
    case "set-password":
      return <PasswordSetStep flow={flow} change={state.change} message={state.message} />;
    case "not-on-team":
      return (
        <>
          <h1>Not on the team yet</h1>
          <p>
            You're signed in as <b>{state.email}</b>, but an admin hasn't added this email. Ask them to add you (Setup, Team), then reload.
          </p>
          <Button onClick={() => void flow.signOut()}>Sign out</Button>
        </>
      );
    case "removed":
      return (
        <>
          <h1>Access removed</h1>
          <p>An admin removed your access. Ask them if that's a mistake.</p>
          <Button onClick={() => void flow.signOut()}>Sign out</Button>
        </>
      );
    case "error":
      return (
        <>
          <h1>Couldn't finish signing in</h1>
          <p>{state.message}</p>
          <Button tone="primary" onClick={() => location.reload()}>Try again</Button>
          <Button onClick={() => void flow.signOut()}>Sign out</Button>
        </>
      );
    case "ready":
      return null;
  }
}

function Msg({ text }: { text?: string | undefined }) {
  return (
    <p className={styles.msg} role={text ? "alert" : undefined}>
      {text ?? ""}
    </p>
  );
}

function PasswordStep({ flow, message }: { flow: AuthFlow; message?: string | undefined }) {
  const [email, setEmail] = useState(lastEmail);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    await flow.signIn(email, password);
    setBusy(false);
  };
  return (
    <>
      <h1>Sign in to Stockroom</h1>
      <form onSubmit={(e) => void submit(e)} autoComplete="on" className={styles.form}>
        <label>
          Email
          <input ref={first} type="email" name="username" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label>
          Password
          <input type="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <Msg text={message} />
        <Button type="submit" tone="primary" size="big" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</Button>
      </form>
      <Button onClick={() => flow.startEmailCode(email)}>First time here, or forgot your password?</Button>
    </>
  );
}

function EmailCodeStep({ flow, email: email0, message }: { flow: AuthFlow; email: string; message?: string | undefined }) {
  const [email, setEmail] = useState(email0);
  const [code, setCode] = useState("");
  const sent = Boolean(message?.startsWith("We sent a code"));
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    await fn();
    setBusy(false);
  };
  return (
    <>
      <h1>{sent ? "Check your email" : "Set your password"}</h1>
      {!sent && <p>First time here, or forgot your password? We'll email you a code. Type it in, then pick a password.</p>}
      <form onSubmit={(e) => { e.preventDefault(); void run(() => (sent ? flow.verifyEmailCode(email, code) : flow.sendEmailCode(email))); }} className={styles.form}>
        <label>
          Email
          <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} readOnly={sent} />
        </label>
        {sent && (
          <label>
            Code from the email
            <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
          </label>
        )}
        <Msg text={message} />
        <Button type="submit" tone="primary" size="big" disabled={busy}>{sent ? "Continue" : "Email me a code"}</Button>
      </form>
      {sent ? <Button onClick={() => flow.startEmailCode(email)}>Use a different email</Button> : <Button onClick={() => flow.backToSignIn()}>Back to sign in</Button>}
    </>
  );
}

function MfaCodeStep({ flow, factorId, message }: { flow: AuthFlow; factorId: string; message?: string | undefined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (c: string) => {
    setBusy(true);
    await flow.verifyMfa(factorId, c);
    setBusy(false);
    setCode("");
  };
  return (
    <>
      <h1>Code from your authenticator app</h1>
      <p>Open your authenticator app and type the 6-digit code for Stockroom.</p>
      <form onSubmit={(e) => { e.preventDefault(); void go(code); }} className={styles.form}>
        <label>
          6-digit code
          <input className={styles.otp} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} autoFocus onChange={(e) => { setCode(e.target.value); if (e.target.value.replace(/\D/g, "").length === 6) void go(e.target.value); }} />
        </label>
        <Msg text={message} />
        <Button type="submit" tone="primary" size="big" disabled={busy}>Verify</Button>
      </form>
      <Button onClick={() => void flow.signOut()}>Sign out</Button>
      <p className="hint">Lost your phone? An admin can reset it in Supabase (Authentication, Users), then you set up a new one.</p>
    </>
  );
}

function EnrolStep({ flow, state }: { flow: AuthFlow; state: Extract<AuthState, { step: "mfa-enrol" }> }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { enrolment, optional, why, message } = state;
  return (
    <>
      <h1>Set up your authenticator app</h1>
      <p>{why}</p>
      <ol className={styles.steps}>
        <li>Install an authenticator app if you don't have one: Google Authenticator, Microsoft Authenticator or Authy (free).</li>
        <li>In the app, add an account and scan this code.</li>
      </ol>
      <img className={styles.qr} src={enrolment.qrCode} alt="QR code to add Stockroom to your authenticator app" />
      <p className="hint">On this phone and can't scan your own screen? Type this key into the app instead:</p>
      <code className={styles.key}>{enrolment.secret}</code>
      <a className={styles.link} href={enrolment.uri}>Or open it in the authenticator app</a>
      <form onSubmit={(e) => { e.preventDefault(); setBusy(true); void flow.finishEnrol(enrolment, code, optional).finally(() => setBusy(false)); }} className={styles.form}>
        <label>
          3. Type the 6-digit code the app shows
          <input className={styles.otp} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} />
        </label>
        <Msg text={message} />
        <Button type="submit" tone="primary" size="big" disabled={busy}>Turn it on</Button>
      </form>
      {optional ? <Button onClick={() => void flow.skipEnrol(enrolment)}>Not now</Button> : <Button onClick={() => void flow.signOut()}>Sign out</Button>}
    </>
  );
}

function PasswordSetStep({ flow, change, message }: { flow: AuthFlow; change: boolean; message?: string | undefined }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <>
      <h1>{change ? "Change your password" : "Pick a password"}</h1>
      <p>
        {change ? "" : "From now on you sign in with your email and this password, then the code from your authenticator app if you have one. "}
        At least 10 characters. A short sentence is easy to remember and hard to guess.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); setBusy(true); void flow.savePassword(a, b, change).finally(() => setBusy(false)); }} className={styles.form}>
        <label>
          New password
          <input type="password" autoComplete="new-password" value={a} onChange={(e) => setA(e.target.value)} autoFocus />
        </label>
        <label>
          Type it again
          <input type="password" autoComplete="new-password" value={b} onChange={(e) => setB(e.target.value)} />
        </label>
        <Msg text={message} />
        <Button type="submit" tone="primary" size="big" disabled={busy}>Save password</Button>
      </form>
    </>
  );
}
